/**
 * Glossary tooltips.
 *
 * Terms come from assets/tooltip-data.json and are turned into
 * <a class="tooltip" data-tooltip="..."> in the page.
 *
 * SCOPE (2026-08-05, Patrick). This used to traverse the whole <body> and link
 * every occurrence of every term, which on a ribbon product page produced seven
 * links, six of them "YMCKO" - in the discontinued banner, in the spec
 * accordion, three times in the description, and once inside a <style> element.
 * Now it links a term the FIRST time it appears, and only inside the body copy
 * a customer is actually reading.
 *
 * Three rules:
 *   1. Only descendants of GLOSSARY_SCOPES are considered.
 *   2. Running prose only. Lists and tables are skipped even inside a scope, so
 *      a term is defined where it is being explained, not in a spec bullet or a
 *      comparison cell (2026-08-05, Patrick).
 *   3. Each glossary entry is linked once per page. Spelling variants that share
 *      a definition (CR80 / CR-80, ID-1 / ID1) count as one entry, so a page
 *      cannot end up with both.
 *
 * To opt another container in, add the class js-glossary to it - no JS change
 * needed.
 */

(function () {
  'use strict';

  /* Where glossary links are allowed. Evaluated in document order. */
  var GLOSSARY_SCOPES = [
    '.product-custom-info .rte',  /* standard product description (13 templates) */
    '.printer-overview',          /* printer + credential-card description */
    '.solution-overview-text',    /* card-printer "why this works" prose */
    '.article-template__content', /* blog article body */
    '.js-glossary'                /* explicit opt-in hook */
  ].join(',');

  /* Never descend into these.

     Headings, links and buttons: a tooltip there fights the element's own job.
     STYLE and NOSCRIPT: the old version injected an anchor into a <style>
     block's text on the product page.
     Lists and tables: spec bullets and comparison cells are for scanning, not
     reading, and a term is far more useful defined in the prose that explains
     it. If a term only ever appears in a bullet, it goes undefined on that page
     by design. */
  var SKIP_TAGS = /^(h1|h2|h3|h4|h5|h6|a|button|script|style|noscript|template|textarea|title|code|pre|kbd|samp|select|option|svg|math|ul|ol|li|dl|dt|dd|table|thead|tbody|tfoot|tr|td|th|caption|figcaption)$/i;

  function escapeRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function initTooltips() {
    var dataUrl = window.__tooltipDataUrl;
    if (!dataUrl) {
      console.warn('Tooltip data URL not set. Tooltips will not work.');
      return;
    }

    fetch(dataUrl)
      .then(function (response) { return response.json(); })
      .then(function (rawTerms) {
        var roots = document.querySelectorAll(GLOSSARY_SCOPES);
        if (!roots.length) return;

        /* Longest first: without this, \bYMCK\b matches the YMCK inside
           "YMCK-UV" (the hyphen is a word boundary) and the longer entry never
           gets its turn. */
        var terms = Array.prototype.slice.call(rawTerms)
          .filter(function (t) { return t && t.word; })
          .sort(function (a, b) { return String(b.word).length - String(a.word).length; })
          .map(function (t) {
            return {
              /* key on the definition, so CR80 and CR-80 share one slot */
              key: String(t.tooltip || t.word),
              url: String(t.url || '#').trim(),
              tooltip: t.tooltip,
              re: new RegExp('\\b' + escapeRe(String(t.word)) + '\\b', 'i')
            };
          });

        var used = Object.create(null);

        function makeLink(term, matchedText) {
          var link = document.createElement('a');
          link.href = term.url === '#' ? 'javascript:void(0);' : term.url;
          link.className = 'tooltip';
          link.setAttribute('data-tooltip', term.tooltip);
          link.textContent = matchedText;
          return link;
        }

        /* Link the earliest still-unused term in this text node, then carry on
           through whatever follows it - a paragraph may legitimately introduce
           two different terms. */
        function processTextNode(node) {
          var text = node.nodeValue;
          if (!text || !text.trim()) return;

          var best = null;
          for (var i = 0; i < terms.length; i++) {
            var term = terms[i];
            if (used[term.key]) continue;
            var m = term.re.exec(text);
            if (m && (best === null || m.index < best.index)) {
              best = { term: term, index: m.index, match: m[0] };
            }
          }
          if (!best || !node.parentNode) return;

          used[best.term.key] = true;

          var frag = document.createDocumentFragment();
          var before = text.slice(0, best.index);
          var after = text.slice(best.index + best.match.length);
          if (before) frag.appendChild(document.createTextNode(before));
          frag.appendChild(makeLink(best.term, best.match));

          var tail = null;
          if (after) {
            tail = document.createTextNode(after);
            frag.appendChild(tail);
          }
          node.parentNode.replaceChild(frag, node);
          if (tail) processTextNode(tail);
        }

        function traverse(node) {
          if (node.nodeType === 3) { processTextNode(node); return; }
          if (node.nodeType !== 1) return;
          if (SKIP_TAGS.test(node.tagName)) return;
          var children = Array.prototype.slice.call(node.childNodes);
          for (var i = 0; i < children.length; i++) traverse(children[i]);
        }

        /* Skip a scope that sits inside a scope already handled, so an opt-in
           .js-glossary nested in a description is not walked twice. */
        var handled = [];
        Array.prototype.forEach.call(roots, function (root) {
          for (var i = 0; i < handled.length; i++) {
            if (handled[i].contains(root)) return;
          }
          handled.push(root);
          traverse(root);
        });

        setTimeout(adjustTooltipPositions, 100);
      })
      .catch(function (err) {
        console.error('Error fetching tooltip data:', err);
        /* The glossary links are gone, but tooltips written directly into the
           markup (the "?" badges on the sample and quote forms) are still on the
           page and still need placing. */
        adjustTooltipPositions();
      });
  }

  /* Keep the bubble inside the viewport.

     The bubble is a ::before that is hidden with visibility, not display, so it
     occupies layout even when nobody can see it. If it is centred on a trigger
     near the right edge it hangs off the page and widens the document - 60px at
     375 on the sample-request and quote-request forms, where the "?" badge is
     pinned to the right of the field. So the side has to be chosen AT REST, not
     on mouseenter: by the time a pointer arrives the page is already too wide,
     and on a touch device mouseenter may never arrive at all.

     Measure against documentElement.clientWidth, NOT window.innerWidth. On an
     already-overflowing page innerWidth reports the widened layout (435 against
     a 375 screen), so a window.innerWidth test silently grants itself the very
     room the overflow created and never fires. */
  var EDGE_GAP = 10;

  function viewportWidth() {
    return document.documentElement.clientWidth || window.innerWidth;
  }

  /* Real rendered width of the bubble. Falls back to the stylesheet's max-width
     if the pseudo-element cannot be measured. */
  function bubbleWidth(tooltip) {
    var w = 0;
    try {
      w = parseFloat(window.getComputedStyle(tooltip, '::before').width);
    } catch (e) { /* older engines: fall through */ }
    return w > 0 ? w : 300;
  }

  function placeTooltip(tooltip) {
    tooltip.classList.remove('tooltip-adjust-left', 'tooltip-adjust-right');

    var rect = tooltip.getBoundingClientRect();
    if (!rect.width && !rect.height) return; /* collapsed or not rendered yet */

    var vw = viewportWidth();
    var width = Math.min(bubbleWidth(tooltip), vw - (EDGE_GAP * 2));
    var centre = rect.left + (rect.width / 2);

    if (centre - (width / 2) < EDGE_GAP) {
      tooltip.classList.add('tooltip-adjust-left');
    } else if (centre + (width / 2) > vw - EDGE_GAP) {
      tooltip.classList.add('tooltip-adjust-right');
    }
  }

  function adjustTooltipPositions() {
    var tooltips = document.querySelectorAll('a.tooltip');

    Array.prototype.forEach.call(tooltips, function (tooltip) {
      /* Always re-place: widths and edges change on rotate and on reflow. The
         old guard returned early here, which made the resize listener a no-op. */
      placeTooltip(tooltip);

      if (tooltip.hasAttribute('data-tooltip-initialized')) return;
      tooltip.setAttribute('data-tooltip-initialized', 'true');

      /* Re-check on the way in as well, for anything whose width settles late
         (late fonts, lazily injected copy). */
      tooltip.addEventListener('mouseenter', function () { placeTooltip(this); });
      tooltip.addEventListener('focus', function () { placeTooltip(this); }, true);
    });
  }

  function start() {
    /* Place the markup tooltips immediately. initTooltips only reaches its own
       adjust pass after the glossary JSON resolves, and it returns early when no
       data URL is set, so neither can be relied on to run this. */
    adjustTooltipPositions();
    initTooltips();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Webfonts land after DOMContentLoaded and change the bubble's width. */
  window.addEventListener('load', adjustTooltipPositions);

  var resizeFrame = null;
  window.addEventListener('resize', function () {
    if (resizeFrame) return;
    resizeFrame = window.requestAnimationFrame(function () {
      resizeFrame = null;
      adjustTooltipPositions();
    });
  });
})();
